// TES-1040 — the admin's read-only support views ("Läkarvy (läsläge)" and
// "Coachvy (läsläge)").
//
// An admin reads the doctor's and the coach's pages as they are, under their
// own path prefix (`/support/doctor/...`, `/support/coach/...`). The prefix is
// the single source of the view: the route tree mounts the pages under it,
// the API client derives the `X-Support-View-As` header from it, and every
// in-app link a clinical page builds is re-prefixed so the admin stays in the
// section she opened.
//
// The server is the real gate: admin is granted GET routes only, and a
// patient-scoped read without a support access grant answers 403
// `support_reason_required`. This module is the client half of that contract.
//
// Pure on purpose (no React, no router, no path aliases): the unit tests in
// `apps/admin/test` run it with plain `node --test`.
import type { SupportViewRole } from "@care-by-ella/contracts";

export type SupportView = SupportViewRole;

/** The path prefix each support view is mounted under. */
export const SUPPORT_VIEW_PREFIX: Record<SupportView, string> = {
  doctor: "/support/doctor",
  coach: "/support/coach",
};

/** Where "/" (the role's home) leads inside each view: the same landing
 *  pages as the doctor's and the coach's own (App.tsx homePathForRole). */
export const SUPPORT_VIEW_HOME: Record<SupportView, string> = {
  doctor: "/support/doctor/today",
  coach: "/support/coach/work-queue",
};

// Mirrors SUPPORT_VIEW_AS_HEADER / SUPPORT_REASON_REQUIRED_ERROR in
// @care-by-ella/contracts. Repeated here because the contracts barrel is a
// runtime import this module must not take (the tests load it without a
// build); test/support-view.test.ts pins the two against each other.
export const SUPPORT_VIEW_HEADER = "X-Support-View-As";
export const SUPPORT_REASON_REQUIRED = "support_reason_required";
export const SUPPORT_REASON_MIN = 5;
export const SUPPORT_REASON_MAX = 500;

/** The support view a path belongs to, or null outside both sections. */
export function supportViewFromPath(pathname: string): SupportView | null {
  for (const view of ["doctor", "coach"] as const) {
    const prefix = SUPPORT_VIEW_PREFIX[view];
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return view;
  }
  return null;
}

/**
 * An in-app path as seen from a view: unchanged for a doctor or coach (no
 * view), prefixed for the admin's support section. Only absolute app paths
 * are rewritten; an external URL or a hash link is left alone.
 */
export function inView(view: SupportView | null, path: string): string {
  if (!view || !path.startsWith("/")) return path;
  const prefix = SUPPORT_VIEW_PREFIX[view];
  if (path === prefix || path.startsWith(`${prefix}/`)) return path;
  return path === "/" ? SUPPORT_VIEW_HOME[view] : `${prefix}${path}`;
}

/** The patient a support-view page is about, from its path. */
export function supportPatientIdFromPath(pathname: string): string | null {
  const view = supportViewFromPath(pathname);
  if (!view) return null;
  const rest = pathname.slice(SUPPORT_VIEW_PREFIX[view].length);
  const match = /^\/patients\/([^/?#]+)/.exec(rest);
  return match ? decodeURIComponent(match[1]) : null;
}

/** The patient an API request is about, from its URL (`…/patients/:id/…`). */
export function patientIdFromApiUrl(url: string): string | null {
  const match = /\/employee-api\/v1\/patients\/([^/?#]+)/.exec(url);
  return match ? decodeURIComponent(match[1]) : null;
}

/** The reason as the API will validate it: trimmed, 5–500 characters. */
export function isSupportReasonValid(reason: string): boolean {
  const len = reason.trim().length;
  return len >= SUPPORT_REASON_MIN && len <= SUPPORT_REASON_MAX;
}

/** "HH:MM" in Stockholm time, for "Åtkomst gäller till HH:MM". */
export function formatAccessUntil(iso: string): string {
  return new Date(iso).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
  });
}

/**
 * Asks the admin for a reason for this patient and grants it. Resolves true
 * once the grant exists, false when she cancels.
 */
export type ReasonPrompt = (patientId: string) => Promise<boolean>;

export interface SupportFetchDeps {
  fetch: typeof fetch;
  /** The current app path, read at request time. */
  currentPath: () => string;
  /** The registered reason dialog, or null when none is mounted. */
  prompt: () => ReasonPrompt | null;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

async function isReasonRequired(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.clone().json()) as { error?: unknown };
    return body?.error === SUPPORT_REASON_REQUIRED;
  } catch {
    return false;
  }
}

/**
 * The fetch every API client goes through.
 *
 * Outside the support sections it is plain fetch (but see TES-1123 below).
 * Inside them it:
 *   - sends `X-Support-View-As` with the section's view, so role-shaped
 *     reads (work queue, handoff queue, check-in trend, cycle view, F-01
 *     start context) answer in the doctor's or the coach's shape;
 *   - answers a 403 `support_reason_required` on a GET (the day's grant
 *     expired mid-session, or a read raced the patient page's own check) by
 *     showing the reason dialog and retrying the read once. Cancelled, the
 *     original 403 is returned and the page shows its usual error.
 *
 * Non-GET requests are never retried: an admin has no mutating route, and a
 * body may not be replayable.
 *
 * TES-1123 — outside the support sections the same 403 turns into the dialog
 * only when a page has registered one: the coach's own patient page does, for
 * a coach who also holds the admin grant and opens a patient she has no
 * coaching basis for. No header is sent there; she reads as the coach. A
 * doctor's or a pure coach's page registers nothing and is unchanged.
 */
export function createSupportFetch(deps: SupportFetchDeps): typeof fetch {
  return async (input, init) => {
    const view = supportViewFromPath(deps.currentPath());
    let sent = init;
    if (view) {
      const headers = new Headers(init?.headers);
      headers.set(SUPPORT_VIEW_HEADER, view);
      sent = { ...init, headers };
    }
    const res = await deps.fetch(input, sent);

    const method = (init?.method ?? "GET").toUpperCase();
    if (method !== "GET" || !(await isReasonRequired(res))) return res;

    const prompt = deps.prompt();
    const patientId =
      patientIdFromApiUrl(urlOf(input)) ??
      supportPatientIdFromPath(deps.currentPath());
    if (!prompt || !patientId) return res;
    const granted = await prompt(patientId);
    return granted ? deps.fetch(input, sent) : res;
  };
}

export interface SupportAccessApi {
  status: (patientId: string) => Promise<{ active: boolean }>;
}

/**
 * Before a patient page loads: does the admin hold today's grant for this
 * patient? If not, ask for a reason (which grants it). True = go ahead and
 * load; false = she cancelled.
 */
export async function ensureSupportAccess(
  patientId: string,
  api: SupportAccessApi,
  prompt: ReasonPrompt,
): Promise<boolean> {
  const { active } = await api.status(patientId);
  if (active) return true;
  return prompt(patientId);
}

/**
 * One dialog per patient at a time. Several reads of the same page can hit
 * the expired grant at once; they all wait on the one dialog instead of
 * stacking one each.
 */
export function dedupePrompt(prompt: ReasonPrompt): ReasonPrompt {
  const pending = new Map<string, Promise<boolean>>();
  return (patientId) => {
    const open = pending.get(patientId);
    if (open) return open;
    const p = prompt(patientId).finally(() => pending.delete(patientId));
    pending.set(patientId, p);
    return p;
  };
}
